import React, { useState, useEffect } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import { Form, Input, Button, message, Row, Col, Card, Space, Select, InputNumber } from 'antd';
import { 
  MailOutlined, 
  LockOutlined,
  UserOutlined,
  IdcardOutlined,
  HomeOutlined,
  BookOutlined,
  PhoneOutlined,
  TeamOutlined,
  BankOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import SuccessModal from './SuccessModal';
import SchoolNameInput from '../../components/SchoolNameInput/SchoolNameInput';
import EducationLevelInput from '../../components/EducationLevelInput/EducationLevelInput';
import { useTranslation } from 'react-i18next';
import './CreateUserForm.css';

const { TextArea } = Input;
const { Option } = Select;

const initialFormData = {
  firstname: '',
  lastname: '',
  nickname: '',
  nationalid: '',
  age: '', 
  email: '',
  password: '',
  role: 'student',
  phone: '',
  address: '',
  currentEducationLevel: '',
  guardianContact: '',
  guardianname: '',
  schoolName: '',
  salesperson_id: ''
};

function CreateStudentForm({ onClose }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [formData, setFormData] = useState(initialFormData);
  const [showPassword, setShowPassword] = useState(true);
  const [salespersons, setSalespersons] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;


    useEffect(() => {
    const fetchSalespersons = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/sales-persons`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSalespersons(res.data || []);
      } catch (error) {
        console.error('❌ Error fetching salespersons:', error);
        message.error('Failed to fetch salespersons');
      }
    };
    fetchSalespersons();
  }, [API_URL]);

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    form.setFieldsValue({ password });
  };

  const handleChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

const handleSubmit = async (values) => {
  try {
    const token = localStorage.getItem('token');

    const formDataObj = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (key === 'linkedStudents') {
        if (Array.isArray(value)) {
          formDataObj.append(key, JSON.stringify(value));
        } else {
        }
      } else if (key === 'age' && typeof value === 'number') {
        formDataObj.append(key, value.toString());
      } else {
        formDataObj.append(key, value ?? '');
      }
    });

    // Ensure role is set to student
    formDataObj.append('role', 'student');

    await axios.post(`${API_URL}/users/create`, formDataObj, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    message.success(t('form.student_created_success'));
    form.resetFields();
    setShowConfirmModal(false);
  } catch (error) {
    console.error('❌ Submission error:', error);
    if (error.response && error.response.data.message === "Email address is already in use") {
      message.error(t('form.email_already_in_use'));
    } else {
      message.error(t('form.create_user_error'));
    }
  }
};


  const showConfirmationModal = (values) => {
    setFormData(values);
    setShowConfirmModal(true);
  };

  return (
    <div className="form-page">
      <Sidebar />
      <Header />
      <div className="form-content">
        <Button type="button" onClick={() => onClose()} style={{ marginBottom: '16px' }}>
          <ArrowLeftOutlined /> {t('back')}
        </Button>

        <Card title={t('form.add_new_student')} bordered={false}>
          <Form 
            form={form}
            layout="vertical" 
            initialValues={initialFormData}
            onFinish={showConfirmationModal}
            scrollToFirstError
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item 
                  name="firstname"
                  label={t('form.first_name')} 
                  rules={[{ required: true, message: t('form.required_field') }]}
                >
                  <Input placeholder={t('form.first_name')} prefix={<UserOutlined />} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item 
                  name="lastname"
                  label={t('form.last_name')} 
                  rules={[{ required: true, message: t('form.required_field') }]}
                >
                  <Input placeholder={t('form.last_name')} prefix={<UserOutlined />} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item 
                  name="nickname"
                  label={t('form.nickname')} 
                  rules={[{ required: true, message: t('form.required_field') }]}
                >
                  <Input placeholder={t('form.nickname')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item 
                  name="gender"
                  label={t('form.select_gender')}
                >
                  <Select placeholder={t('form.select_gender')} allowClear>
                    <Option value="male">{t('male')}</Option>
                    <Option value="female">{t('female')}</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item 
                  name="nationalid"
                  label={t('form.national_id')}
                >
                  <Input prefix={<IdcardOutlined />} placeholder={t('form.national_id')} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item 
                  name="age"
                  label={t('Age')}
                  rules={[{ required: false, type: 'number', min: 1, max: 100 }]}
                >
                  <InputNumber
                    placeholder={t('Age')}
                    style={{ width: '100%' }}
                    onChange={(value) => handleChange("age", value)}
                  />
                </Form.Item>
              </Col>
            </Row>

                        <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  name="currentEducationLevel"
                  label={<Space><BookOutlined /> {t('form.current_education_level')}</Space>}
                >
                  <EducationLevelInput placeholder={t('form.education_level')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="schoolName"
                  label={<Space><BankOutlined /> {t('form.school_name')}</Space>}
                >
                  <SchoolNameInput placeholder={t('form.school_name')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  name="email"
                  label={t('form.email')}
                  rules={[
                    { required: true, message: t('form.required_field') },
                    { type: 'email', message: t('form.invalid_email') }
                  ]}
                >
                  <Input prefix={<MailOutlined />} placeholder={t('form.email')} />
                </Form.Item>
              </Col>
                  <Col span={12}>
                    <Form.Item
                      name="password"
                      label={t('form.temporary_password')}
                      rules={[{ required: true, message: t('form.required_field') }]}
                    >
                      <Input.Password
                        prefix={<LockOutlined />}
                        visibilityToggle={{
                          visible: showPassword,
                          onVisibleChange: setShowPassword,
                        }}
                        placeholder={t('form.temporary_password')}
                        addonAfter={
                          <Button type="link" onClick={generateRandomPassword}>
                            {t('form.generate_password')}
                          </Button>
                        }
                      />
                    </Form.Item>
                  </Col>


            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  name="phone"
                  label={t('form.phone')}
                >
                  <Input prefix={<PhoneOutlined />} placeholder={t('form.phone')} />
                </Form.Item>
              </Col>
               <Col span={12}>
                <Form.Item
                  name="salesperson_id"
                  label={t('form.salesperson')}
                  rules={[{ required: false }]}
                >
                  <Select placeholder={t('form.select_salesperson')} allowClear>
                    {salespersons.map(sp => (
                      <Option key={sp.id} value={sp.id}>
                        {sp.full_name} {sp.nickname ? `(${sp.nickname})` : ''} - {sp.email}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item 
              name="address"
              label={<Space><HomeOutlined /> {t('form.address')}</Space>}
            >
              <TextArea rows={3} placeholder={t('form.address')} />
            </Form.Item>



            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  name="guardianname"
                  label={<Space><TeamOutlined /> {t('form.parent_name')}</Space>}
                >
                  <Input placeholder={t('form.enter_guardian_name')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="guardianContact"
                  label={<Space><PhoneOutlined /> {t('form.parent_phone')}</Space>}
                >
                  <Input placeholder={t('form.parent_phone')} />
                </Form.Item>
              </Col>
            </Row>

            {/* Dynamic list for linked students */}
            <Form.List name="linkedStudents">
              {(fields, { add, remove }) => (
                <>
                  <h3>{t('form.additional_students')}</h3>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Card key={key} type="inner" title={`${t('form.student')} ${index + 2}`} style={{ marginBottom: 16 }}>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'firstname']}
                            label={t('form.first_name')}
                            rules={[{ required: true, message: t('form.required_field') }]}
                          >
                            <Input placeholder={t('form.first_name')} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'lastname']}
                            label={t('form.last_name')}
                            rules={[{ required: true, message: t('form.required_field') }]}
                          >
                            <Input placeholder={t('form.last_name')} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'nickname']}
                            label={t('form.nickname')}
                            rules={[{ required: true, message: t('form.required_field') }]}
                          >
                            <Input placeholder={t('form.nickname')} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'currentEducationLevel']}
                            label={t('form.current_education_level')}
                          >
                            <EducationLevelInput placeholder={t('form.education_level')} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'schoolName']}
                            label={t('form.school_name')}
                          >
                            <SchoolNameInput placeholder={t('form.school_name')} />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, 'gender']}
                            label={t('form.select_gender')}
                          >
                            <Select allowClear placeholder={t('form.select_gender')}>
                              <Option value="male">{t('male')}</Option>
                              <Option value="female">{t('female')}</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, 'age']}
                            label={t('Age')}
                          >
                            <InputNumber style={{ width: '100%' }} min={1} max={100} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Button danger onClick={() => remove(name)} type="link">
                        {t('form.remove_student')}
                      </Button>
                    </Card>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block>
                      + {t('form.add_more_student')}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>


            <Form.Item style={{ marginTop: 24 }}>
              <Button type="primary" htmlType="submit" size="large">
                {t('form.create_student')}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {showConfirmModal && (
          <SuccessModal 
            details={formData} 
            onClose={() => setShowConfirmModal(false)} 
            onConfirm={() => {
              handleSubmit(formData);
              setShowConfirmModal(false);
            }} 
          />
        )}
      </div>
    </div>
  );
}

export default withRoleAccess(CreateStudentForm, ['admin', 'superadmin']);
